/**
 * SceneGenerationJobTasks 页 Table 行展开面板 —— 精工克制版。
 *
 * 视觉基调(refined minimalism):
 * - 整个抽屉一张卡,内部用**发丝隔线**分 3 段(不是 3 张同质白卡)
 * - 顶部 hero 行:mono 大字号 sceneId + name,右侧大号状态胶囊(4 档语义色)
 * - 备注文本:蓝色左竖条标记「输入源」,不套边框
 * - 日志:暗色 pre 保留(天然暗色语境),顶部信息 chip 化
 * - 豁免:inline 表单 —— 输入 + 按钮并排;不允许时禁用输入 + 灰字提示,不用大 Alert
 *
 * 3 块内容:
 * 1. 场景备注:GET /task/{taskId}/scene-info
 * 2. 详细日志:GET /task/logs
 * 3. 豁免:POST /scene-inspection/{sceneId}/waive
 */
import { useEffect, useState } from 'react';
import { App, Button, Input, Popconfirm, Spin } from 'antd';
import { ReloadOutlined, CopyOutlined, DownloadOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import {
  getTaskLogs,
  getTaskSceneInfo,
  waiveScene,
  type TaskLogResponse,
  type TaskSceneInfo,
} from '@/apis/simulation';

const MONO =
  '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace';

interface Props {
  taskId: number;
  onWaived?: () => void;
}

/** 场景状态 4 档语义色(与 antd Tag 里的 color token 保持一致语义):
 *  - 1 已录入:平静灰
 *  - 5 质检中:蓝(processing 感)
 *  - 7 已完成-瑕疵品:琥珀(通过但有瑕疵)
 *  - 8 已完成:绿(success)
 *  - 9 不合格:红(需要处理)
 */
const SCENE_STATUS_TONE: Record<number, { fg: string; bg: string; bar: string }> = {
  1:  { fg: '#4b5563', bg: '#f3f4f6', bar: '#9ca3af' },
  5:  { fg: '#1d4ed8', bg: '#eff6ff', bar: '#3b82f6' },
  7:  { fg: '#b45309', bg: '#fffbeb', bar: '#f59e0b' },
  8:  { fg: '#166534', bg: '#f0fdf4', bar: '#22c55e' },
  9:  { fg: '#b91c1c', bg: '#fef2f2', bar: '#ef4444' },
};
const FALLBACK_TONE = { fg: '#6b7280', bg: '#f9fafb', bar: '#d1d5db' };

export default function SceneGenTaskDetailPanel({ taskId, onWaived }: Props) {
  const { message } = App.useApp();
  const [sceneInfo, setSceneInfo] = useState<TaskSceneInfo | null>(null);
  const [sceneInfoLoading, setSceneInfoLoading] = useState(false);
  const [logData, setLogData] = useState<TaskLogResponse | null>(null);
  const [logLoading, setLogLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [waiving, setWaiving] = useState(false);

  const loadSceneInfo = async () => {
    setSceneInfoLoading(true);
    try {
      setSceneInfo(await getTaskSceneInfo(taskId));
    } catch (e) {
      message.error((e as { message?: string })?.message || '拉取场景备注失败');
    } finally {
      setSceneInfoLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogLoading(true);
    try {
      setLogData(await getTaskLogs(taskId));
    } catch (e) {
      message.error((e as { message?: string })?.message || '拉取日志失败');
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => {
    loadSceneInfo();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const handleWaive = async () => {
    if (!sceneInfo) return;
    setWaiving(true);
    try {
      const result = await waiveScene(sceneInfo.sceneId, reason.trim() || undefined);
      // releasedDisable（R3）：被「重新生成失败」禁用的场景，豁免时后端会一并解除禁用。
      const tail = result.releasedDisable ? '，已解除禁用' : '';
      const tip = result.autoAddedToNodeName
        ? `已豁免为瑕疵品,并加入场景集「${result.autoAddedToNodeName}」${tail}`
        : `已豁免为瑕疵品${tail}`;
      message.success(tip);
      setSceneInfo({
        ...sceneInfo,
        sceneStatus: result.status,
        sceneStatusName: result.statusName,
      });
      setReason('');
      onWaived?.();
    } catch (e) {
      message.error((e as { message?: string })?.message || '豁免失败');
    } finally {
      setWaiving(false);
    }
  };

  const canWaive = sceneInfo?.sceneStatus === 9;
  const tone = sceneInfo
    ? SCENE_STATUS_TONE[sceneInfo.sceneStatus] ?? FALLBACK_TONE
    : FALLBACK_TONE;

  const handleCopySceneInfo = () => {
    if (!sceneInfo?.sceneInfo) return;
    navigator.clipboard?.writeText(sceneInfo.sceneInfo).then(
      () => message.success('已复制'),
      () => message.error('复制失败'),
    );
  };

  const handleCopyS3 = () => {
    if (!logData?.logS3Path) return;
    navigator.clipboard?.writeText(logData.logS3Path).then(
      () => message.success('已复制 S3 路径'),
      () => message.error('复制失败'),
    );
  };

  return (
    <div className="sgt-drawer">
      <Spin spinning={sceneInfoLoading && !sceneInfo}>
        {/* ── 顶部 hero:场景标识 + 状态 ── */}
        <div className="sgt-hero">
          <div className="sgt-hero__ident">
            <span className="sgt-hero__label">场景</span>
            <span className="sgt-hero__id">
              {sceneInfo ? `#${sceneInfo.sceneId}` : '—'}
            </span>
            {sceneInfo?.sceneName && (
              <span className="sgt-hero__name">{sceneInfo.sceneName}</span>
            )}
          </div>
          {sceneInfo && (
            <span
              className="sgt-status"
              style={{ background: tone.bg, color: tone.fg }}
            >
              <span
                className="sgt-status__bar"
                style={{ background: tone.bar }}
              />
              {sceneInfo.sceneStatusName || sceneInfo.sceneStatus}
            </span>
          )}
        </div>

        {/* ── 1. 备注 ── */}
        <section className="sgt-section">
          <div className="sgt-section__head">
            <span className="sgt-section__title">备注</span>
            {sceneInfo?.sceneInfo && (
              <button
                type="button"
                className="sgt-icon-btn"
                onClick={handleCopySceneInfo}
                title="复制备注"
              >
                <CopyOutlined />
              </button>
            )}
          </div>
          <div className="sgt-note">
            {sceneInfo?.sceneInfo ? (
              <pre className="sgt-note__body">{sceneInfo.sceneInfo}</pre>
            ) : (
              <span className="sgt-note__empty">
                {sceneInfoLoading ? '加载中…' : '(无备注)'}
              </span>
            )}
          </div>
        </section>

        {/* ── 2. 详细日志 ── */}
        <section className="sgt-section">
          <div className="sgt-section__head">
            <span className="sgt-section__title">详细日志</span>
            <div className="sgt-section__extra">
              {logData && (
                <span className="sgt-chip">
                  {logData.totalLines || 0} 行 · 尾部 {logData.tailLines || 0} 行
                </span>
              )}
              {logData?.logS3Path && (
                <button
                  type="button"
                  className="sgt-icon-btn"
                  onClick={handleCopyS3}
                  title={`S3: ${logData.logS3Path}`}
                >
                  <CopyOutlined />
                </button>
              )}
              {logData?.logDownloadUrl && (
                <a
                  className="sgt-icon-btn"
                  href={logData.logDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="下载完整日志"
                >
                  <DownloadOutlined />
                </a>
              )}
              <button
                type="button"
                className="sgt-icon-btn"
                onClick={loadLogs}
                title="刷新日志"
                disabled={logLoading}
              >
                <ReloadOutlined spin={logLoading} />
              </button>
            </div>
          </div>
          <Spin spinning={logLoading && !logData}>
            <pre className="sgt-log">
              {logData?.logContent || (logLoading ? '' : '(日志为空)')}
            </pre>
          </Spin>
        </section>

        {/* ── 3. 豁免 ── */}
        <section className="sgt-section sgt-section--waive">
          <div className="sgt-section__head">
            <span className="sgt-section__title">豁免</span>
            <span className="sgt-section__hint">
              {canWaive
                ? '豁免后场景从「不合格」→「已完成-瑕疵品」,若 Job 已关联场景集会自动入集'
                : sceneInfo
                  ? `当前状态「${sceneInfo.sceneStatusName || sceneInfo.sceneStatus}」不可豁免`
                  : ''}
            </span>
          </div>
          <div className="sgt-waive">
            <Input
              placeholder={canWaive ? '豁免原因(可选)' : '仅不合格场景可豁免'}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!canWaive}
              prefix={
                !canWaive && sceneInfo ? (
                  <ExclamationCircleFilled style={{ color: '#bfbfbf' }} />
                ) : undefined
              }
              onPressEnter={canWaive ? handleWaive : undefined}
              style={{ flex: 1 }}
            />
            <Popconfirm
              title="确认豁免这个场景?"
              description={
                <div style={{ fontSize: 12, maxWidth: 260 }}>
                  状态将从「不合格」置为「已完成-瑕疵品」,原因将追加到场景描述。
                </div>
              }
              onConfirm={handleWaive}
              okText="确认豁免"
              cancelText="取消"
              disabled={waiving || !canWaive}
            >
              <Button
                type="primary"
                danger
                loading={waiving}
                disabled={!canWaive}
              >
                豁免为瑕疵品
              </Button>
            </Popconfirm>
          </div>
        </section>
      </Spin>

      {/* 组件私有样式:BEM-lite 命名,不污染全局。 */}
      <style>{`
        .sgt-drawer {
          background: #fff;
          border: 1px solid #eef0f2;
          border-radius: 8px;
          overflow: hidden;
          margin: -4px 0;
        }

        /* ── hero ── */
        .sgt-hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          background: linear-gradient(180deg, #fbfbfc 0%, #f7f8fa 100%);
          border-bottom: 1px solid #eef0f2;
          gap: 12px;
        }
        .sgt-hero__ident {
          display: flex;
          align-items: baseline;
          gap: 10px;
          min-width: 0;
          flex: 1;
        }
        .sgt-hero__label {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .sgt-hero__id {
          font-family: ${MONO};
          font-size: 15px;
          font-weight: 600;
          color: #111827;
          font-variant-numeric: tabular-nums;
        }
        .sgt-hero__name {
          font-size: 13px;
          color: #4b5563;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ── 状态胶囊(左侧色条 + 右侧文字) ── */
        .sgt-status {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 4px 12px 4px 4px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.4;
          overflow: hidden;
          flex-shrink: 0;
        }
        .sgt-status__bar {
          display: inline-block;
          width: 3px;
          height: 16px;
          border-radius: 2px;
        }

        /* ── section 通用 ── */
        .sgt-section {
          padding: 14px 20px;
          border-bottom: 1px solid #f0f2f4;
        }
        .sgt-section:last-child { border-bottom: none; }
        .sgt-section--waive { background: #fafbfc; }
        .sgt-section__head {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        .sgt-section__title {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          letter-spacing: 0.05em;
        }
        .sgt-section__hint {
          font-size: 11.5px;
          color: #9ca3af;
          margin-left: 6px;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .sgt-section__extra {
          margin-left: auto;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .sgt-chip {
          font-size: 11px;
          color: #6b7280;
          padding: 2px 8px;
          background: #f3f4f6;
          border-radius: 10px;
          font-variant-numeric: tabular-nums;
        }

        /* ── icon button(hover 才出显色) ── */
        .sgt-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          padding: 0;
          border: none;
          background: transparent;
          color: #9ca3af;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.14s ease, color 0.14s ease;
          text-decoration: none;
        }
        .sgt-icon-btn:hover:not([disabled]) {
          background: #f3f4f6;
          color: #4b5563;
        }
        .sgt-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── 备注块:左侧蓝竖条 + 无边框文本 ── */
        .sgt-note {
          position: relative;
          padding: 4px 0 4px 12px;
          border-left: 2px solid #dbeafe;
        }
        .sgt-note__body {
          margin: 0;
          font-family: ${MONO};
          font-size: 12.5px;
          line-height: 1.6;
          color: #374151;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .sgt-note__empty {
          font-size: 12.5px;
          color: #bfbfbf;
          font-style: italic;
        }

        /* ── log 暗色 ── */
        .sgt-log {
          margin: 0;
          padding: 12px 14px;
          background: #0b1120;
          color: #cbd5e1;
          border-radius: 6px;
          max-height: 340px;
          min-height: 60px;
          overflow: auto;
          font-family: ${MONO};
          font-size: 11.5px;
          line-height: 1.55;
          white-space: pre;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
        }
        /* 定制暗色滚动条,不用浏览器默认(会跟白背景反差过强) */
        .sgt-log::-webkit-scrollbar { width: 8px; height: 8px; }
        .sgt-log::-webkit-scrollbar-thumb {
          background: #334155;
          border-radius: 4px;
        }
        .sgt-log::-webkit-scrollbar-thumb:hover { background: #475569; }
        .sgt-log::-webkit-scrollbar-track { background: transparent; }

        /* ── 豁免 inline 表单 ── */
        .sgt-waive {
          display: flex;
          gap: 8px;
          align-items: center;
        }
      `}</style>
    </div>
  );
}
